import { Article, Event, Head, StructuredData } from '@/components';
import { PermutationPageRenderer } from '@/components/SectionListRenderer';
import {
  ArticleTypeName,
  CONNECTING_TO_CONTENTFUL,
  CONNECTING_TO_REDIS,
  ContentTypeName,
  ThunkDebugName,
  serverSharedData,
} from '@/constants';
import { getAllEventThumbnails, getAllThumbnails, getArticle, getEvent, getTag } from '@/fetchers';
import {
  utils_getFeaturedEventList,
  utils_getRelatedArticles,
  utils_getRelatedEvents,
  utils_getRightSectionList,
  utils_relatedCampaignEventsObj,
} from '@/fetchers/utils';
import type { IArticleProps, IEvent, ITag, IThumbnailSection, ThumbnailProps } from '@/interfaces';
import { capitalize, checkIsPreviewModeRequest, getArticleTypeNameFromSlug } from '@/utils';
import { getArticleStructuredData, removeOneArticleSensitiveData } from '@/utils/articleDataModify.utils';
import createServerSidePropsResult, { type IThunk } from '@/utils/createServerSidePropsResult.utils';
import { getEventStructuredData, removeOneEventSensitiveData } from '@/utils/eventDataModify.utils';
import type { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import getConfig from 'next/config';

const { serverRuntimeConfig } = getConfig();

const ArticlePage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  articlePermutationContent,
  articleContent,
  eventContent,
  relatedEvents,
  relatedArticles,
  relatedCampaignEvents,
  campaignTitle,
  featuredEventList,
  webVersion,
}) => {
  // biome-ignore lint/suspicious/noConsoleLog: <explanation>
  console.log(webVersion);

  if (articlePermutationContent) {
    return (
      <>
        {articlePermutationContent.seo && <Head {...articlePermutationContent.seo} />}
        <PermutationPageRenderer
          sectionList={articlePermutationContent.sectionList || []}
          title={articlePermutationContent.title}
          featuredEventList={featuredEventList}
          rightSectionList={articlePermutationContent.rightSectionList}
          breadcrumbsTheme={articlePermutationContent.breadcrumbsTheme}
        />
      </>
    );
  }

  if (articleContent) {
    return (
      <>
        {articleContent.seo && <Head {...articleContent.seo} />}
        <StructuredData data={getArticleStructuredData(articleContent)} />
        <Article
          articleContent={articleContent}
          relatedArticles={relatedArticles || []}
          rightSectionList={articleContent.rightSectionList}
        />
      </>
    );
  }

  if (eventContent) {
    return (
      <>
        {eventContent.seo && <Head {...eventContent.seo} />}
        <StructuredData data={getEventStructuredData(eventContent)} />
        <Event
          eventContent={eventContent}
          relatedEvents={relatedEvents}
          relatedCampaignEvents={relatedCampaignEvents}
          campaignTitle={campaignTitle}
          rightSectionList={eventContent.rightSectionList}
        />
      </>
    );
  }

  return null;
};

interface IArticlePageServerSideProps {
  // Article permutation = Tag
  articlePermutationContent: ITag | null;
  articleContent: IArticleProps | null;
  eventContent: IEvent | null;
  relatedEvents: ThumbnailProps[] | null;
  relatedCampaignEvents: ThumbnailProps[] | null;
  campaignTitle: string | null;
  relatedArticles: ThumbnailProps[] | null;
  featuredEventList: IEvent[] | null;
  webVersion: string;
}

interface IArticleParams extends NodeJS.Dict<string> {
  articleTypeSlug: string;
  slug: string;
}

type Thunk = IThunk<IArticlePageServerSideProps, IArticleParams>;

const getRedisUtils: Thunk = async ({ nextJsContext, customContext }) => {
  if (!customContext.serverRuntimeConfig.useDev) {
    const redisUtils = await import('@/utils/redis.utils');

    customContext.getServerPropsCached = redisUtils.getServerPropsCached;
    customContext.cacheServerProps = redisUtils.cacheServerProps;
    customContext.setUrlById = redisUtils.setUrlById;
  }

  nextJsContext.res.setHeader('Cache-Control', 'public, s-maxage=43200, stale-while-revalidate=60');
};

getRedisUtils.debugName = ThunkDebugName.GetRedisUtils;

const getServerPropsCached: Thunk = async ({ nextJsContext, customContext }) => {
  const { params, resolvedUrl } = nextJsContext;
  const { serverSideProps, getServerPropsCached } = customContext;
  const { articleTypeSlug = '' } = params || {};
  const articleTypeName = getArticleTypeNameFromSlug(articleTypeSlug);
  customContext.articleTypeName = articleTypeName;

  if (!articleTypeName) {
    return {
      props: {
        ...serverSideProps,
      },
      notFound: true,
    };
  }

  const pageURL = resolvedUrl.split('?')[0];
  customContext.pageURL = pageURL;

  const serverPropsCached: IArticlePageServerSideProps = JSON.parse((await getServerPropsCached(pageURL)) || 'null');
  if (serverPropsCached) {
    const { articleContent, eventContent } = serverPropsCached;

    if (!serverSharedData.isLoggedIn && eventContent) {
      serverPropsCached.eventContent = removeOneEventSensitiveData(eventContent);
    }

    if (!serverSharedData.isLoggedIn && articleContent) {
      serverPropsCached.articleContent = removeOneArticleSensitiveData(articleContent);
    }

    return {
      props: {
        ...serverPropsCached,
        webVersion: CONNECTING_TO_REDIS,
      },
    };
  }
};

getServerPropsCached.debugName = ThunkDebugName.GetServerPropsCached;

const getEventContent: Thunk = async ({ nextJsContext, customContext }) => {
  const { query, params } = nextJsContext;
  const { serverSideProps, setUrlById, cacheServerProps, pageURL, articleTypeName } = customContext;

  const slug = params?.slug || '';
  customContext.slug = slug;

  const isPreviewMode = checkIsPreviewModeRequest(query);
  customContext.isPreviewMode = isPreviewMode;

  const isEventPage = articleTypeName === ArticleTypeName.events;
  const [allEvents, allArticles]: [any, any] = await Promise.all([
    getAllEventThumbnails({ isPreviewMode, slug: pageURL }),
    getAllThumbnails({ isPreviewMode, slug: pageURL }),
  ]);

  customContext.allEvents = allEvents;
  customContext.allArticles = allArticles;

  if (isEventPage) {
    serverSideProps.eventContent = await getEvent({ isPreviewMode, slug });
  }

  if (serverSideProps.eventContent) {
    serverSideProps.eventContent.rightSectionList = utils_getRightSectionList({
      articles: allArticles,
      events: allEvents,
      data: serverSideProps.eventContent.rightSectionList,
      currentEventId: serverSideProps.eventContent.sys.id,
    });

    const tagList = serverSideProps.eventContent.tags?.map((tag) => tag.slug) || [];

    const unModifyTagList = serverSideProps.eventContent.tags || [];

    const currentEventId = serverSideProps.eventContent.sys.id;

    const relatedCampaignEventsObj = utils_relatedCampaignEventsObj({
      events: allEvents,
      tagList: unModifyTagList,
      currentEventSysId: currentEventId,
      showCampaign: serverSideProps.eventContent.showCampaign,
    });
    serverSideProps.relatedEvents = utils_getRelatedEvents({
      events: allEvents,
      tagList: tagList.slice(0, 2),
      currentEventSysId: currentEventId,
    });
    serverSideProps.relatedCampaignEvents = relatedCampaignEventsObj?.relatedCampaignEvents || null;
    serverSideProps.campaignTitle = relatedCampaignEventsObj?.campaignTitle || null;

    if (!isPreviewMode) {
      setUrlById(currentEventId, pageURL);
      cacheServerProps(pageURL, serverSideProps);
    }

    if (!serverSharedData.isLoggedIn) {
      serverSideProps.eventContent = removeOneEventSensitiveData(serverSideProps.eventContent);
    }

    return {
      props: {
        ...serverSideProps,
      },
    };
  }
};

getEventContent.debugName = 'GET_EVENT_CONTENT';

const getArticleContent: Thunk = async ({ customContext }) => {
  const { slug } = customContext;
  const {
    serverSideProps,
    setUrlById,
    cacheServerProps,
    allEvents,
    allArticles,
    isPreviewMode,
    articleTypeName,
    pageURL,
  } = customContext;

  serverSideProps.articleContent =
    (await getArticle({
      isPreviewMode,
      articleTypeName,
      slug,
    })) || null;

  if (serverSideProps.articleContent) {
    serverSideProps.articleContent.rightSectionList = utils_getRightSectionList({
      articles: allArticles,
      events: allEvents,
      data: serverSideProps.articleContent.rightSectionList,
    });

    serverSideProps.relatedArticles =
      utils_getRelatedArticles({
        articles: allArticles,
        currentArticleTags: serverSideProps.articleContent.tags,
        currentArticleSysId: serverSideProps.articleContent.sys.id,
      }) || null;

    if (!isPreviewMode) {
      setUrlById(serverSideProps.articleContent.sys.id, pageURL);
      cacheServerProps(pageURL, serverSideProps);
    }

    if (!serverSharedData.isLoggedIn) {
      serverSideProps.articleContent = removeOneArticleSensitiveData(serverSideProps.articleContent);
    }

    return {
      props: {
        ...serverSideProps,
      },
    };
  }
};

getArticleContent.debugName = 'GET_ARTICLE_CONTENT';

const getArticlePermutationContent: Thunk = async ({ customContext }) => {
  const {
    serverSideProps,
    isPreviewMode,
    articleTypeName,
    allEvents,
    allArticles,
    slug,
    setUrlById,
    cacheServerProps,
    pageURL,
  } = customContext;
  const articlePermutationContent = await getTag({
    isPreviewMode,
    articleTypeName,
    slug,
    allEventsList: allEvents,
    allArticlesList: allArticles,
  });

  if (articlePermutationContent?.hide) {
    return {
      notFound: true,
    };
  }

  serverSideProps.articlePermutationContent = articlePermutationContent;

  if (serverSideProps.articlePermutationContent) {
    serverSideProps.articlePermutationContent.rightSectionList = utils_getRightSectionList({
      articles: allArticles,
      events: allEvents,
      data: serverSideProps.articlePermutationContent.rightSectionList,
    });

    const isFeaturedEventList =
      articleTypeName === 'events' &&
      serverSideProps.articlePermutationContent.sectionList?.some(
        (section: any) => capitalize(section.__typename) === ContentTypeName['EventList'] && !!section.featured,
      );

    serverSideProps.featuredEventList =
      (isFeaturedEventList && utils_getFeaturedEventList({ events: allEvents, slug })) || [];

    const foundThumbnailSection: IThumbnailSection = serverSideProps.articlePermutationContent.sectionList?.find(
      (section: any) => capitalize(section.__typename) === ContentTypeName.ThumbnailSection,
    );

    if (serverSideProps.featuredEventList && foundThumbnailSection) {
      foundThumbnailSection.latestData = foundThumbnailSection.latestData.filter(
        (thumbnailItem) =>
          !serverSideProps.featuredEventList?.find(
            (featuredEventItem) => featuredEventItem.sys.id === thumbnailItem.sys.id,
          ),
      );

      foundThumbnailSection.featuredEventList = serverSideProps.featuredEventList;
    }

    if (foundThumbnailSection) {
      const totalThumbnails = foundThumbnailSection.latestData.length;
      const totalPages = Math.ceil(totalThumbnails / 10);

      foundThumbnailSection.pageInfo = {
        itemsPerPage: 10,
        totalPages,
      };

      foundThumbnailSection.latestData = foundThumbnailSection.latestData.slice(0, 10);
    }

    if (!isPreviewMode) {
      setUrlById(serverSideProps.articlePermutationContent.sys.id, pageURL);
      cacheServerProps(pageURL, serverSideProps);
    }

    return {
      props: {
        ...serverSideProps,
      },
    };
  }

  return {
    props: {
      ...serverSideProps,
    },
    notFound: true,
  };
};

getArticlePermutationContent.debugName = 'GET_ARTICLE_PERMUTATION_CONTENT';

export const getServerSideProps: GetServerSideProps<IArticlePageServerSideProps, IArticleParams> = async (context) => {
  return createServerSidePropsResult<IArticlePageServerSideProps, IArticleParams>({
    context,
    customContext: {
      serverSideProps: {
        articlePermutationContent: null,
        articleContent: null,
        eventContent: null,
        relatedEvents: null,
        relatedCampaignEvents: null,
        campaignTitle: null,
        relatedArticles: null,
        featuredEventList: [],
        webVersion: CONNECTING_TO_CONTENTFUL,
      },
      serverRuntimeConfig,
      getServerPropsCached: () => null,
      cacheServerProps: () => null,
      setUrlById: () => null,
    },
    thunks: [getRedisUtils, getServerPropsCached, getEventContent, getArticleContent, getArticlePermutationContent],
  });
};

export default ArticlePage;
